import { Box, Button, Page } from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import { usersApi } from 'apis/users';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useCurrentUser } from 'utils/user';

import { profileActions } from '../../settingsProfile.actions';

import { ProfileDesktopAppLink } from './components/info/components/desktopAppLink/profileDesktopAppLink';
import { ProfileFormInfo } from './components/info/profileFormInfo';
import { ProfileFormFieldValues } from './profileForm.types';

export const SettingsProfileForm = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const notify = useNotifications();
  const { user } = useCurrentUser();

  const form = useForm<ProfileFormFieldValues>({
    mode: 'all',
    defaultValues: {
      given_name: user.given_name ?? '',
      family_name: user.family_name ?? '',
      name: user.name ?? '',
      email: user.email ?? '',
      phone_number: user.phone_number ?? '',
    },
  });

  const [updateUser] = usersApi.useUpdateUserMutation();

  const handleValidSubmit: SubmitHandler<ProfileFormFieldValues> = async (
    values,
  ) => {
    try {
      const { data } = await updateUser({
        id: user.id,
        patch: {
          updates: {
            display_name: values.name,
            given_name: values.given_name,
            family_name: values.family_name,
            ...(values.phone_number && { phone_number: values.phone_number }),
          },
          deletes: [
            ...(values.phone_number ? [] : (['phone_number'] as const)),
          ],
        },
      }).unwrap();

      dispatch(profileActions.update({ user: data }));

      ampli.settingsProfileFormSubmitted({
        payload: {},
      });

      form.reset(values);

      notify(
        {
          title: 'Success',
          text: `Your profile has been updated.`,
          icon: 'done',
          time: 5000,
        },
        NotificationType.Success,
      );
    } catch (error) {
      notify(
        {
          title: 'Error while saving your profile.',
        },
        NotificationType.Error,
      );
    }
  };

  const { formState } = form;

  const pageControls = (
    <>
      <Box padding="0 300">
        <ProfileDesktopAppLink />
      </Box>
      <Button
        type="submit"
        form={settingsProfileFormId}
        isLoading={formState.isSubmitting}
        disabled={
          !formState.isDirty || !formState.isValid || formState.isSubmitting
        }
      >
        Save changes
      </Button>
    </>
  );

  return (
    <FormProvider {...form}>
      <form
        id={settingsProfileFormId}
        noValidate
        tabIndex={-1}
        onSubmit={form.handleSubmit(handleValidSubmit)}
      >
        <Page>
          <Page.Head title="Profile" controls={pageControls} />

          <Page.Body>
            <ProfileFormInfo />
          </Page.Body>
        </Page>
      </form>
    </FormProvider>
  );
};

export const settingsProfileFormId = 'profile';
